import { useEffect } from "react";
import * as React from "react";
import { useFormatTemplateStore } from "@/state/formatTemplateStore";
import { getSlideImagesId, IS_SLIDE_IMAGES_ID, removeSlideImagesId } from "@/utils/formatTemplate/slideImages";
import { bufferToBase64Image, fetchPotxSlideImages } from "@/utils/formatTemplate/slides-extraction-handlers";

// Local implementation of useEffectAsync for Excel compatibility
const useEffectAsync = (func: () => Promise<void | (() => void)>, dependencies: any[]) => {
  useEffect(() => {
    let cleanup: (() => void) | void;

    const runAsync = async () => {
      cleanup = await func();
    };

    runAsync();

    return () => {
      if (cleanup && typeof cleanup === "function") {
        cleanup();
      }
    };
  }, dependencies);
};
import { Skeleton, Empty } from "antd";
import { useDragAndDrop } from "@formkit/drag-and-drop/react";
import { animations } from "@formkit/drag-and-drop";
import { fetchFormatStyleData, parseTheme } from "@/utils/formatTemplate/fetchFormatStyleData";

type SlideCategory = {
  id: string;
  title: string;
  slides: any[];
  skeletonCount: number;
  minHeight?: string;
};

export default function PPTSlidesPreview() {
  const slideImages = useFormatTemplateStore((state) => state.slideImages);
  const isLoading = useFormatTemplateStore((state) => state.isLoading);
  const setLoading = useFormatTemplateStore((state) => state.setIsloading);
  const setSlideImages = useFormatTemplateStore((state) => state.setSlideImages);
  const { updateAnswered } = useFormatTemplateStore();
  const updateImageCategory = useFormatTemplateStore((state) => state.updateImageCategory);

  const [categories, setCategories] = React.useState<SlideCategory[]>([
    {
      id: "front-covers",
      title: "Front Covers",
      slides: [],
      skeletonCount: 2,
      minHeight: "150px",
    },
    {
      id: "agenda",
      title: "Agenda / TOC",
      slides: [],
      skeletonCount: 1,
      minHeight: "150px",
    },
    {
      id: "dividers",
      title: "Section Dividers",
      slides: [],
      skeletonCount: 3,
      minHeight: "150px",
    },
    {
      id: "content",
      title: "Content Slides",
      slides: [],
      skeletonCount: 6,
      minHeight: "150px",
    },
    {
      id: "disclaimers",
      title: "Disclaimers",
      slides: [],
      skeletonCount: 3,
      minHeight: "150px",
    },
    {
      id: "other",
      title: "Disclaimers",
      slides: [],
      skeletonCount: 3,
      minHeight: "150px",
    },
  ]);

  // Create individual drag-and-drop states for each category
  const [frontCoversList, frontCovers, setFrontCovers] = useDragAndDrop<HTMLDivElement, any>([], {
    group: "slides",
    plugins: [animations()],
    dragHandle: ".drag-handle",
    dropZoneClass: "drop-zone-active", // Added for empty drop zones
    handleNodeDrop: (data) => {
      updateImageCategory(data.targetData.node.data.value.name, "Front Covers");
    },
  });

  const [agendaList, agenda, setAgenda] = useDragAndDrop<HTMLDivElement, any>([], {
    group: "slides",
    plugins: [animations()],
    dragHandle: ".drag-handle",
    dropZoneClass: "drop-zone-active",
    handleNodeDrop: (data) => {
      updateImageCategory(data.targetData.node.data.value.name, "Agenda / Table of Contents / Executive Summary");
    },
  });

  const [dividersList, dividers, setDividers] = useDragAndDrop<HTMLDivElement, any>([], {
    group: "slides",
    plugins: [animations()],
    dragHandle: ".drag-handle",
    dropZoneClass: "drop-zone-active",
    handleNodeDrop: (data) => {
      updateImageCategory(data.targetData.node.data.value.name, "Section Dividers");
    },
  });

  const [contentList, content, setContent] = useDragAndDrop<HTMLDivElement, any>([], {
    group: "slides",
    plugins: [animations()],
    dragHandle: ".drag-handle",
    dropZoneClass: "drop-zone-active",
    handleNodeDrop: (data) => {
      updateImageCategory(data.targetData.node.data.value.name, "Content Slides");
    },
  });

  const [disclaimersList, disclaimers, setDisclaimers] = useDragAndDrop<HTMLDivElement, any>([], {
    group: "slides",
    plugins: [animations()],
    dragHandle: ".drag-handle",
    dropZoneClass: "drop-zone-active",
    handleNodeDrop: (data) => {
      updateImageCategory(data.targetData.node.data.value.name, "Disclaimers and Closing");
    },
  });

  // Update drag-and-drop states when slideImages change
  React.useEffect(() => {
    if (slideImages?.length > 0) {
      const grouped = {
        "Front Covers": [] as any[],
        "Agenda / Table of Contents / Executive Summary": [],
        "Section Dividers": [],
        "Content Slides": [],
        "Disclaimers and Closing": [],
      };

      slideImages.forEach((img: any) => {
        const cat = img.category?.trim() ?? "";
        if (cat.includes("Front Cover")) grouped["Front Covers"].push(img);
        else if (cat.includes("Agenda")) grouped["Agenda / Table of Contents / Executive Summary"].push(img);
        else if (cat.includes("Divider")) grouped["Section Dividers"].push(img);
        else if (cat.includes("Disclaimer") || cat.includes("Closing")) grouped["Disclaimers and Closing"].push(img);
        else grouped["Content Slides"].push(img);
      });

      setFrontCovers(grouped["Front Covers"]);
      setAgenda(grouped["Agenda / Table of Contents / Executive Summary"]);
      setDividers(grouped["Section Dividers"]);
      setContent(grouped["Content Slides"]);
      setDisclaimers(grouped["Disclaimers and Closing"]);

      setCategories([
        { ...categories[0], slides: grouped["Front Covers"] },
        { ...categories[1], slides: grouped["Agenda / Table of Contents / Executive Summary"] },
        { ...categories[2], slides: grouped["Section Dividers"] },
        { ...categories[3], slides: grouped["Content Slides"] },
        { ...categories[4], slides: grouped["Disclaimers and Closing"] },
      ]);
    } else {
      setFrontCovers([]);
      setAgenda([]);
      setDividers([]);
      setContent([]);
      setDisclaimers([]);
      setCategories(categories.map((cat) => ({ ...cat, slides: [] })));
    }
  }, [slideImages]);

  const syncFromStorage = async () => {
    const fileId = getSlideImagesId();
    if (fileId) {
      try {
        setLoading(true);
        const images = await fetchPotxSlideImages(fileId);
        images.forEach((img: any) => {
          updateImageCategory(img.name, img.category || "Other");
        });
        setSlideImages(images);
        const styleMetaData = await fetchFormatStyleData(fileId);
        const { chartColors, tableColors, fontsSection } = parseTheme(styleMetaData);
        const { updateFontsSection, updateColorsSection, currentStep, setEditApprovals } =
          useFormatTemplateStore.getState();
        // Base font config
        updateFontsSection("fontStyle", fontsSection.fontStyle);
        updateFontsSection("fontColor", fontsSection.fontColor);
        updateFontsSection("fontSize", fontsSection.fontSize);
        // Detailed roles
        updateFontsSection("placeholderTitle", fontsSection.placeholderTitle);
        updateFontsSection("placeholderSubTitle", fontsSection.placeholderSubTitle);
        updateFontsSection("slideTitle", fontsSection.slideTitle);
        updateFontsSection("slideSubTitle", fontsSection.slideSubTitle);
        updateFontsSection("keyTakeAways", fontsSection.keyTakeAways);
        updateFontsSection("placeholderAxisLabels", fontsSection.placeholderAxisLabels);
        updateFontsSection("source", fontsSection.source);
        updateFontsSection("footnotes", fontsSection.footnotes);
        updateColorsSection("chartColors", chartColors);
        updateColorsSection("tableColors", tableColors);
        setEditApprovals([]);
        updateAnswered(currentStep, 1);
      } catch (error) {
        console.error("Error loading slides:", error);
      } finally {
        setLoading(false);
        removeSlideImagesId();
      }
    }
  };

  useEffectAsync(async () => {
    await syncFromStorage();
    const handleStorage = async (event: StorageEvent) => {
      if (event.key === IS_SLIDE_IMAGES_ID) {
        await syncFromStorage();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const getImageSrc = (img: any) => bufferToBase64Image(img?.buffer?.data);

  const renderSkeletons = (count: number) =>
    Array.from({ length: count }).map((_, idx) => (
      <Skeleton.Image key={`skeleton-${idx}`} active style={{ width: "100%", height: 160 }} />
    ));

  const renderSlide = (img: any, idx: number) => {
    const slideKey = img?.id || img?.buffer?.data?.toString() || idx;
    return (
      <div key={`slide-${slideKey}`} className="kanban-item text-center">
        <div className="drag-handle cursor-move">
          <img
            src={getImageSrc(img)}
            className="w-full h-auto border rounded shadow"
            draggable="false"
            alt={`Slide ${idx + 1}`}
          />
          <p className="mt-1 text-sm text-gray-600">Slide {idx + 1}</p>
        </div>
      </div>
    );
  };

  if (!isLoading && (!slideImages || slideImages.length === 0)) {
    return (
      <div className="p-4 pt-7">
        <Empty description={<span>No slides found. Please upload a .potx template file first.</span>} />
      </div>
    );
  }

  // Helper function to render each category section
  const renderCategory = (
    title: string,
    ref: React.RefObject<HTMLDivElement>,
    items: any[],
    skeletonCount: number,
    minHeight?: string
  ) => (
    <div className="mb-8">
      <h2 className="text-xl font-bold mb-2">{title}</h2>
      <div ref={ref} className="grid grid-cols-3 gap-4" style={{ minHeight }}>
        {isLoading ? renderSkeletons(skeletonCount) : items.map(renderSlide)}
      </div>
    </div>
  );

  return (
    <div className="p-4 pt-7">
      <p className="text-sm text-gray-700 mb-3">
        From the <code>.potx</code> Presentation Template you provided, we divided the custom slides into relevant
        categories of slides below. Please adjust the slides in each category as appropriate.
      </p>

      {renderCategory("Front Covers", frontCoversList, frontCovers, 2, categories[0].minHeight)}
      {renderCategory("Agenda / Table of Contents / Executive Summary", agendaList, agenda, 1, categories[1].minHeight)}
      {renderCategory("Section Dividers", dividersList, dividers, 3, categories[2].minHeight)}
      {renderCategory("Content Slides", contentList, content, 6, categories[3].minHeight)}
      {renderCategory("Disclaimers and Closing", disclaimersList, disclaimers, 3, categories[4].minHeight)}
    </div>
  );
}
